import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { changeTenantStatus, getMaintenanceToken } from '../../store/slices/tenantSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { FiCheck, FiX, FiAlertTriangle, FiTool, FiCopy } from 'react-icons/fi';

interface TenantStatusManagerProps {
    tenantId: string;
    // 'provisioning' added so this component can render the state Phase 1 introduced
    // (Tenant.status already carries it — models/Tenant.ts / tenantApi.ts:197). No transition
    // logic added for it here: the provisioning -> active flip is the readiness panel's
    // "Activate tenant" action (permanently disabled today, see TenantReadinessPanel), not
    // this manual status override. That stays out of scope for this change.
    currentStatus: 'active' | 'disabled' | 'maintenance' | 'provisioning' | 'deleted';
    onStatusChanged?: () => void;
}

const TenantStatusManager: React.FC<TenantStatusManagerProps> = ({
    tenantId,
    currentStatus,
    onStatusChanged
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [isChanging, setIsChanging] = useState(false);
    const [newStatus, setNewStatus] = useState<'active' | 'disabled' | 'maintenance'>(
        currentStatus === 'deleted' ? 'disabled' : currentStatus as any
    );
    const [reason, setReason] = useState('');
    const [maintenanceToken, setMaintenanceToken] = useState<string | null>(null);
    const [showTokenDialog, setShowTokenDialog] = useState(false);

    // Can't change status if tenant is deleted
    if (currentStatus === 'deleted') {
        return (
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                <div className="flex items-center text-yellow-600 dark:text-yellow-400">
                    <FiAlertTriangle className="h-5 w-5 mr-2" />
                    <p>Cannot change status of a deleted tenant. Restore the tenant first.</p>
                </div>
            </div>
        );
    }

    const handleStatusChange = async () => {
        if (newStatus === currentStatus) {
            setIsChanging(false);
            return;
        }

        try {
            setIsChanging(true);

            // If changing to maintenance mode and including token in response
            const includeToken = newStatus === 'maintenance';

            await dispatch(changeTenantStatus({
                id: tenantId,
                statusData: { status: newStatus, reason },
                includeToken
            })).unwrap();

            // If moving to maintenance mode, get the token
            if (newStatus === 'maintenance') {
                try {
                    const response = await dispatch(getMaintenanceToken(tenantId)).unwrap();
                    setMaintenanceToken(response);
                    setShowTokenDialog(true);
                } catch (error) {
                    console.error('Failed to get maintenance token:', error);
                }
            }

            dispatch(addNotification({
                type: 'success',
                message: `Tenant status changed to ${newStatus} successfully`
            }));

            // Call the callback if provided
            if (onStatusChanged) {
                onStatusChanged();
            }
        } catch (error: any) {
            dispatch(addNotification({
                type: 'error',
                title: 'Status Change Failed',
                message: error.message || 'Failed to change tenant status'
            }));
        } finally {
            setIsChanging(false);
            setReason('');
        }
    };

    const copyTokenToClipboard = () => {
        if (maintenanceToken) {
            navigator.clipboard.writeText(maintenanceToken)
                .then(() => {
                    dispatch(addNotification({
                        type: 'info',
                        message: 'Maintenance token copied to clipboard'
                    }));
                })
                .catch(err => {
                    console.error('Could not copy text: ', err);
                });
        }
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
            case 'disabled':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
            case 'maintenance':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
            case 'provisioning':
                // Matches the existing 'provisioning' colour convention in DashboardPage.tsx.
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Status:</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${getStatusBadgeColor(currentStatus)}`}>
                    {currentStatus}
                </span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Change Status</h3>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            New Status
                        </label>
                        <select
                            id="status"
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value as any)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                            disabled={isChanging}
                        >
                            <option value="active">Active</option>
                            <option value="disabled">Disabled</option>
                            <option value="maintenance">Maintenance</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Reason (optional)
                        </label>
                        <textarea
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            className="mt-1 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter a reason for the status change"
                            disabled={isChanging}
                        />
                    </div>

                    {newStatus === 'maintenance' && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <FiTool className="h-5 w-5 text-yellow-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Maintenance Mode</h3>
                                    <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-200">
                                        <p>
                                            Enabling maintenance mode will generate a special access token that administrators
                                            can use to access the tenant while it's in maintenance. This token will be displayed
                                            after the status is changed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => setIsChanging(false)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            disabled={isChanging}
                        >
                            <FiX className="mr-2 -ml-1 h-5 w-5" />
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleStatusChange}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            disabled={isChanging || newStatus === currentStatus}
                        >
                            {isChanging ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <FiCheck className="mr-2 -ml-1 h-5 w-5" />
                                    Change Status
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Maintenance Token Dialog */}
            {showTokenDialog && maintenanceToken && (
                <div className="fixed inset-0 overflow-y-auto z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div>
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900">
                                    <FiTool className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />
                                </div>
                                <div className="mt-3 text-center sm:mt-5">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                                        Maintenance Token Generated
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            This token allows administrators to access the tenant while it's in maintenance mode.
                                            Keep it secure and do not share it with unauthorized individuals.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5 sm:mt-6">
                                <div className="relative mt-1 rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        className="block w-full pr-10 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500 rounded-md"
                                        value={maintenanceToken}
                                        readOnly
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <button
                                            type="button"
                                            onClick={copyTokenToClipboard}
                                            className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                                        >
                                            <FiCopy className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>

                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    This token will be valid until the tenant is moved out of maintenance mode.
                                </p>

                                <div className="mt-5">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
                                        onClick={() => setShowTokenDialog(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantStatusManager;
